import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../lib/api';
import { useOrg } from '../../../hooks/useOrg';
import ConfirmDialog from '../../shared/ConfirmDialog';
import FieldPlacementEditor from '../FieldPlacementEditor';
import FormPreview from '../FormPreview';

export default function FormTemplatesTab() {
  const { currentOrg } = useOrg();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'initial', frequency: 'once' });
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getFormTemplates(currentOrg.id);
      setTemplates(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await api.uploadFormDocument(currentOrg.id, file);
      setUploadResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!uploadResult) { setError('Please upload a document'); return; }
    setSaving(true);
    setError('');
    try {
      await api.createFormTemplate(currentOrg.id, {
        name: form.name,
        type: form.type,
        frequency: form.frequency === 'manual' ? null : form.frequency,
        document_url: uploadResult.url,
        page_count: uploadResult.pageCount,
        field_placements: [],
        fields: [],
      });
      setForm({ name: '', type: 'initial', frequency: 'once' });
      setUploadResult(null);
      setShowAdd(false);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateFrequency = async (t, nextFrequency) => {
    try {
      await api.updateFormTemplate(currentOrg.id, t.id, {
        frequency: nextFrequency === 'manual' ? null : nextFrequency,
      });
      fetchTemplates();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteFormTemplate(currentOrg.id, confirmDelete.id);
      setConfirmDelete(null);
      fetchTemplates();
    } catch (err) {
      console.error(err);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div>
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-primary-c">E-Sign Forms</h3>
          <p className="text-[12px] text-muted-c mt-0.5">Upload your intake forms. Patients sign them electronically with the appointment date auto-filled.</p>
        </div>
        {!showAdd && (
          <button onClick={() => { setShowAdd(true); setUploadResult(null); setError(''); }}
            className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all whitespace-nowrap shrink-0">
            + Upload Form
          </button>
        )}
      </div>

      {/* Upload + create */}
      {showAdd && (
        <div className="bg-tertiary rounded-xl border border-theme p-5 mb-4 animate-fade-in">
          <h4 className="text-[13px] font-semibold text-primary-c mb-3">New Form Template</h4>
          {error && <div className="px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium mb-3">{error}</div>}

          <div className="space-y-3">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Form Name</label>
              <input type="text" className={inputClass} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Initial Patient Intake" />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Form Type</label>
              <div className="flex gap-2">
                {[{ value: 'initial', label: 'Initial Intake', desc: 'First visit' }, { value: 'follow_up', label: 'Follow-Up', desc: 'Return visits' }].map(t => (
                  <button key={t.value} type="button" onClick={() => setForm(p => ({ ...p, type: t.value }))}
                    className={`flex-1 p-3 rounded-[10px] text-left border transition-all ${form.type === t.value ? 'border-accent bg-accent-glow' : 'border-strong bg-elevated hover:bg-hover-surface'}`}>
                    <div className="text-[13px] font-semibold text-primary-c">{t.label}</div>
                    <div className="text-[11px] text-muted-c">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Send to patient</label>
              <div className="grid gap-2">
                {[
                  { value: 'once', label: 'Once (onboarding)', desc: 'Auto-sent the first time the patient books an appointment with this practice.' },
                  { value: 'every_appointment', label: 'Every appointment', desc: 'A fresh copy is sent every time the patient books an appointment.' },
                  { value: 'manual', label: 'Manual only', desc: 'Only sent when staff explicitly assigns it.' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, frequency: opt.value }))}
                    className={`p-3 rounded-[10px] text-left border transition-all ${form.frequency === opt.value ? 'border-accent bg-accent-glow' : 'border-strong bg-elevated hover:bg-hover-surface'}`}
                  >
                    <div className="text-[13px] font-semibold text-primary-c">{opt.label}</div>
                    <div className="text-[11px] text-muted-c mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Upload Document</label>
              {uploadResult ? (
                <div className="flex items-center gap-3 p-3 bg-success-soft rounded-[10px] border border-[rgba(16,185,129,0.3)]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  <div className="flex-1">
                    <div className="text-[12px] font-semibold text-primary-c">Document uploaded</div>
                    <div className="text-[11px] text-muted-c">{uploadResult.pageCount} page{uploadResult.pageCount !== 1 ? 's' : ''}</div>
                  </div>
                  <button onClick={() => setUploadResult(null)} className="text-[11px] text-muted-c hover:text-danger">Change</button>
                </div>
              ) : (
                <div className="relative">
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleUpload} disabled={uploading}
                    className="absolute inset-0 opacity-0 cursor-pointer" />
                  <div className={`flex items-center justify-center gap-2 p-6 border-2 border-dashed border-strong rounded-[10px] text-[13px] text-muted-c hover:border-accent-light hover:text-accent-light transition-all ${uploading ? 'opacity-50' : 'cursor-pointer'}`}>
                    {uploading ? (
                      <><div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" /> Converting...</>
                    ) : (
                      <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Drop PDF or Word document here</>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-c mt-1.5">Supports PDF, DOC, DOCX. Max 20MB. Word files are converted to PDF.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowAdd(false); setError(''); setUploadResult(null); }}
              className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-primary-c bg-elevated border border-theme hover:bg-hover-surface transition-all">
              Cancel
            </button>
            <button onClick={handleCreate} disabled={saving}
              className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </div>
      )}

      {/* Template list */}
      {loading ? (
        <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-tertiary rounded-xl animate-pulse" />)}</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-10 bg-tertiary rounded-xl border border-theme">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-muted-c mb-3 opacity-40"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <p className="text-[13px] text-muted-c">No form templates yet</p>
          <p className="text-[11px] text-muted-c mt-1">Upload your first intake form to start sending it to patients</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className="bg-elevated rounded-xl border border-theme p-4 flex items-center gap-3.5 group">
              <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center flex-shrink-0 ${t.type === 'initial' ? 'bg-accent-glow' : 'bg-success-soft'}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.type === 'initial' ? '#0DA7CA' : '#10B981'} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[13px] font-semibold text-primary-c truncate">{t.name}</div>
                  {t.frequency === 'once' && (
                    <span className="px-1.5 py-0.5 rounded-[6px] bg-accent-glow text-accent-light text-[10px] font-semibold uppercase tracking-wide">Onboarding</span>
                  )}
                  {t.frequency === 'every_appointment' && (
                    <span className="px-1.5 py-0.5 rounded-[6px] bg-success-soft text-success text-[10px] font-semibold uppercase tracking-wide">Every visit</span>
                  )}
                </div>
                <div className="text-[11px] text-muted-c mt-0.5">
                  {t.type === 'initial' ? 'Initial Intake' : 'Follow-Up'}
                  {t.page_count > 0 && ` · ${t.page_count} page${t.page_count !== 1 ? 's' : ''}`}
                  {t.document_url && ' · PDF uploaded'}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end max-w-[46%] md:max-w-none md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <select
                  value={t.frequency || 'manual'}
                  onChange={e => updateFrequency(t, e.target.value)}
                  className="px-2 py-1 rounded-lg text-[11px] font-medium text-primary-c bg-tertiary border border-theme"
                >
                  <option value="once">Once (onboarding)</option>
                  <option value="every_appointment">Every appointment</option>
                  <option value="manual">Manual only</option>
                </select>
                {t.document_url && (
                  <button onClick={() => setEditingTemplate(t)} className="text-[11px] font-semibold text-accent-light hover:underline">Place Fields</button>
                )}
                {t.document_url && (
                  <button onClick={() => setPreviewTemplate(t)} className="text-[11px] font-medium text-muted-c hover:text-accent-light">Preview</button>
                )}
                <button onClick={() => setConfirmDelete(t)} className="text-[11px] font-medium text-muted-c hover:text-danger">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Form Template"
        message={`Delete "${confirmDelete?.name}"? Existing signed forms will be kept.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      {previewTemplate && (
        <FormPreview template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}

      {editingTemplate && (
        <FieldPlacementEditor
          template={editingTemplate}
          onSave={() => { setEditingTemplate(null); fetchTemplates(); }}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </div>
  );
}
